import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Ticket, Plus, Minus, ShoppingCart, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useTicketCart } from '../../context/TicketCartContext';

import { API_URL } from '../../utils/api';

export default function TicketPurchaseModal({ isOpen, onClose, eventId, eventTitle }) {
  const [ticketInfo, setTicketInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTickets, setSelectedTickets] = useState({});
  const { addToCart, setCartOpen } = useTicketCart();

  useEffect(() => {
    if (isOpen && eventId) {
      fetchTicketInfo();
    }
  }, [isOpen, eventId]);

  const fetchTicketInfo = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tickets/public/event/${eventId}`);
      if (res.ok) {
        const data = await res.json();
        setTicketInfo(data);
        // Initialize selection state
        const initial = {};
        data.ticket_types?.forEach(tt => {
          initial[tt.ticket_type_id] = 0;
        });
        setSelectedTickets(initial);
      } else {
        toast.error('Failed to load ticket information');
      }
    } catch (error) {
      console.error('Error fetching ticket info:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (ticketTypeId, delta, maxPerOrder, available) => {
    setSelectedTickets(prev => {
      const current = prev[ticketTypeId] || 0;
      const newVal = Math.max(0, Math.min(current + delta, maxPerOrder, available));
      return { ...prev, [ticketTypeId]: newVal };
    });
  };

  const handleAddToCart = () => {
    const itemsToAdd = Object.entries(selectedTickets)
      .filter(([_, qty]) => qty > 0)
      .map(([ticketTypeId, quantity]) => {
        const tt = ticketInfo.ticket_types.find(t => t.ticket_type_id === ticketTypeId);
        return {
          event_id: eventId,
          event_title: ticketInfo.event_title || eventTitle,
          ticket_type_id: ticketTypeId,
          ticket_type_name: tt.name,
          price: tt.price,
          quantity
        };
      });
    
    if (itemsToAdd.length === 0) {
      toast.error('Please select at least one ticket');
      return;
    }

    itemsToAdd.forEach(item => addToCart(item));
    toast.success('Added to basket!');
    
    // Reset selections
    const reset = {};
    ticketInfo.ticket_types?.forEach(tt => {
      reset[tt.ticket_type_id] = 0;
    });
    setSelectedTickets(reset);
    
    onClose();
    setCartOpen(true);
  };

  const getTotalSelected = () => {
    return Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);
  };

  const getSelectedTotal = () => {
    if (!ticketInfo?.ticket_types) return 0;
    return Object.entries(selectedTickets).reduce((total, [id, qty]) => {
      const tt = ticketInfo.ticket_types.find(t => t.ticket_type_id === id);
      return total + (tt?.price || 0) * qty;
    }, 0);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden shadow-2xl"
          onClick={e => e.stopPropagation()}
          data-testid="ticket-purchase-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Ticket className="w-6 h-6 text-white" />
              <h2 className="text-xl font-bold text-white">Get Tickets</h2>
            </div>
            <button 
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="close-ticket-modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(85vh-180px)]">
            {loading ? (
              <div className="py-12 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-500 mx-auto mb-4"></div>
                <p className="text-slate-400">Loading tickets...</p>
              </div>
            ) : !ticketInfo?.ticketing_enabled ? (
              <div className="py-12 text-center">
                <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Tickets are not available for this event</p>
              </div>
            ) : ticketInfo.ticket_types?.length === 0 ? (
              <div className="py-12 text-center">
                <Ticket className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No tickets available yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-slate-400 text-sm mb-4">
                  Select your tickets for <span className="text-white font-medium">{ticketInfo.event_title || eventTitle}</span>
                </p>

                {ticketInfo.ticket_types.map(tt => {
                  const isAvailable = !tt.is_sold_out && !tt.sale_not_started && !tt.sale_ended;
                  const currentQty = selectedTickets[tt.ticket_type_id] || 0;
                  const maxQty = Math.min(tt.max_per_order || 10, tt.available);

                  return (
                    <div 
                      key={tt.ticket_type_id}
                      className={`p-4 rounded-xl border transition-colors ${
                        isAvailable 
                          ? 'bg-slate-800/50 border-slate-700 hover:border-violet-500/50' 
                          : 'bg-slate-800/30 border-slate-800 opacity-60'
                      }`}
                      data-testid={`ticket-type-${tt.ticket_type_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-white">{tt.name}</h3>
                            {tt.is_sold_out && (
                              <Badge variant="destructive" className="text-xs">Sold Out</Badge>
                            )}
                            {tt.sale_not_started && (
                              <Badge variant="secondary" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Coming Soon
                              </Badge>
                            )}
                            {tt.sale_ended && (
                              <Badge variant="secondary" className="text-xs">Sale Ended</Badge>
                            )}
                          </div>
                          {tt.description && (
                            <p className="text-slate-400 text-sm mb-2">{tt.description}</p>
                          )}
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-emerald-400 font-bold text-lg">£{tt.price.toFixed(2)}</span>
                            {isAvailable && (
                              <span className="text-slate-400">
                                {tt.available} available
                              </span>
                            )}
                          </div>
                        </div>

                        {isAvailable && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleQuantityChange(tt.ticket_type_id, -1, maxQty, tt.available)}
                              disabled={currentQty === 0}
                              className="w-9 h-9 rounded-full bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                              data-testid={`decrease-${tt.ticket_type_id}`}
                            >
                              <Minus className="w-4 h-4 text-white" />
                            </button>
                            <span className="w-8 text-center font-semibold text-white text-lg" data-testid={`qty-${tt.ticket_type_id}`}>
                              {currentQty}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(tt.ticket_type_id, 1, maxQty, tt.available)}
                              disabled={currentQty >= maxQty}
                              className="w-9 h-9 rounded-full bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                              data-testid={`increase-${tt.ticket_type_id}`}
                            >
                              <Plus className="w-4 h-4 text-white" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Fee notice */}
                {!ticketInfo.absorb_fees && (
                  <p className="text-slate-400 text-xs mt-4">
                    A booking fee of £{ticketInfo.platform_fee?.toFixed(2) || '0.50'} per ticket will be added at checkout.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {ticketInfo?.ticketing_enabled && ticketInfo?.ticket_types?.length > 0 && (
            <div className="border-t border-slate-800 p-5 bg-slate-900/80">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm">Selected</p>
                  <p className="text-white font-semibold">
                    {getTotalSelected()} ticket{getTotalSelected() !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-sm">Subtotal</p>
                  <p className="text-emerald-400 font-bold text-xl">£{getSelectedTotal().toFixed(2)}</p>
                </div>
              </div>
              
              <Button
                onClick={handleAddToCart}
                disabled={getTotalSelected() === 0}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold py-3 rounded-xl disabled:opacity-50"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Basket
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
